import { useMemo } from "react";

import {
  QueryOptions,
  useGetChildActiveSubscriptions
} from "seneca-common/features/parents/features/parent/service/hooks/useFetchChildrenSubscriptions";
import {
  subscriptionProductExists as doesSubscriptionProductExists,
  getSubscriptionTierName,
  subscriptionProductHasParentPremiumFeatures
} from "seneca-common/features/subscriptions/features/subscription-products/state";
import { SUBSCRIPTION_TIERS } from "seneca-common/features/subscriptions/features/subscription-products/state/models/state/subscription-products/subscription-product/productDescription/ProductDescription";
import Subscription, {
  OptionalSubscriptionType
} from "seneca-common/features/subscriptions/state/models/subscriptions/subscription/Subscription";
import {
  getProductId,
  hasStripeSubscription,
  isManaged
} from "seneca-common/features/subscriptions/state/selectors/state/subscriptions/subscription";
import { useSelectorOnConditionWithArgs } from "seneca-common/utils/hooks/selectors";

import findSubscriptionToShow from "../pages/monitoring-overview/components/utils/findSubscriptionToShow";

export default function useChildSubscriptionInfo(
  childId: string,
  options?: QueryOptions
) {
  const { data: activeSubs = [], isLoading } = useGetChildActiveSubscriptions(
    childId,
    options
  );
  const activeSubscriptions = useMemo(
    () =>
      activeSubs.map(sub => new Subscription(sub as OptionalSubscriptionType)),
    [activeSubs]
  );

  const subscription = findSubscriptionToShow(
    [hasStripeSubscription, isManaged],
    activeSubscriptions
  );

  const isPremium = !!subscription;

  const hasMIS = isPremium && isManaged(subscription);

  const subscriptionProductId = isPremium && getProductId(subscription);

  const subscriptionProductExists = useSelectorOnConditionWithArgs(
    !!subscriptionProductId,
    doesSubscriptionProductExists,
    subscriptionProductId
  );

  const tierName = useSelectorOnConditionWithArgs(
    subscriptionProductExists,
    getSubscriptionTierName,
    subscriptionProductId
  ) as SUBSCRIPTION_TIERS | null;

  const productHasParentPremiumFeatures = useSelectorOnConditionWithArgs(
    subscriptionProductExists,
    subscriptionProductHasParentPremiumFeatures,
    subscriptionProductId
  );

  return {
    tierName,
    isPremium,
    hasMIS,
    canSubscriptionBeTransferredTo: !isPremium || hasMIS,
    isLoading,
    hasParentPremiumFeatures: productHasParentPremiumFeatures && !hasMIS
  };
}
